import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { DebugOverlay, useDebug } from "../../../debug";
import { useAuth } from "../../../contexts/AuthContext";
import { useAttendanceLoader } from "../hooks/useAttendanceLoader";
import { useAttendanceSave } from "../hooks/useAttendanceSave";
import { useAttendanceActions } from "../hooks/useAttendanceActions";
import { DialogHeaderSection } from "./DialogHeaderSection";
import { OverviewSection } from "./OverviewSection";
import { EditSection } from "./EditSection";

import type {
  CourseOverlap,
  AttendanceDialogProps,
} from "../types/attendanceDialog";

export type { CourseOverlap, AttendanceDialogProps };

export function AttendanceDialog({
  open,
  onOpenChange,
  lessonId,
  viewMode = "edit",
  onSaved,
  initialBundle,
  courseOverlaps,
  userIdOverride,
  schoolIdOverride,
  skipPrefillSummary,
}: AttendanceDialogProps) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const { userProfile } = useAuth();

  // ── State + load/polling/prefill/protection ────────────────────────
  const {
    selectedLesson: rawSelectedLesson,
    attendanceViewMode,
    setAttendanceViewMode,
    overviewData,
    lessonNote,
    setLessonNote,
    lessonHomework,
    setLessonHomework,
    initialLessonHomework,
    tempAttendance,
    setTempAttendance,
    explicitPresentIdsRef,
    setEditedStudentIds,
    protectionByStudent,
    overrideReasons,
    lessonStatusOverrides,
    prefilledFromPrevious,
    prefilledAny,
    recordedMeta,
    diaryMeta,
    canEditExisting,
    badgeCounts,
    kbStudentIds,
    kbStudentTimes,
    earlierPresentIds,
    concurrentCourseAttendance,
    setConcurrentCourseAttendance,
    nextLessonCourses,
    nextLessonIsCancelled,
    nextLessonSubject,
    keptInMainLesson,
    pendingTransfer,
    setPendingTransfer,
    transferReason,
    setTransferReason,
    transferredStudentIds,
    setTransferredStudentIds,
    frozenEditOrderRef,
    frozenEditLessonIdRef,
    suppressRealtimeRef,
    pauseRealtime,
    reloadBundle,
    transferStudent,
    isTransferring,
  } = useAttendanceLoader({
    open,
    lessonId,
    viewMode,
    initialBundle,
    courseOverlaps,
    schoolIdOverride,
    userIdOverride,
  });

  // ── Merge in guest students from the bundle's `guests` field ───────
  // get_attendance_bundle_v2 now returns distributed guests in one round-trip.
  const selectedLesson = useMemo(() => {
    if (!rawSelectedLesson) return rawSelectedLesson;
    const guests = (rawSelectedLesson as { guests?: any[] }).guests || [];
    if (guests.length === 0) return rawSelectedLesson;

    const existingIds = new Set(
      ((rawSelectedLesson.students as any[]) || []).map((s: any) => s.id),
    );
    const guestRows = guests
      .filter((g) => !existingIds.has(g.student_id))
      .map((g) => ({
        id: g.student_id,
        name: `${g.first_name ?? ""} ${g.last_name ?? ""}`.trim(),
        isGuest: true,
        guestSourceClassName: g.source_class_name ?? null,
        guestSourceLabel: g.source_label ?? null,
      }));

    if (guestRows.length === 0) return rawSelectedLesson;

    return {
      ...rawSelectedLesson,
      students: [
        ...((rawSelectedLesson.students as any[]) || []),
        ...guestRows,
      ],
    };
  }, [rawSelectedLesson]);

  // ── Save logic ─────────────────────────────────────────────────────
  const { saveAttendance } = useAttendanceSave({
    selectedLesson,
    tempAttendance,
    lessonStatusOverrides,
    overrideReasons,
    lessonNote,
    lessonHomework,
    initialLessonHomework,
    onSaved,
    onOpenChange,
    suppressRealtimeRef,
    pauseRealtime,
    userIdOverride,
  });

  // ── Action callbacks ───────────────────────────────────────────────
  const {
    formatStudentName,
    setStudentStatus,
    setAllStudentsStatus,
    switchToEdit,
    handleTransferConfirm,
    handleReturnToCourseConfirm,
    handleStayedWithClassTransfer,
  } = useAttendanceActions({
    selectedLesson,
    overviewData,
    tempAttendance,
    setTempAttendance,
    setEditedStudentIds,
    setAttendanceViewMode,
    setLessonNote,
    protectionByStudent,
    explicitPresentIdsRef,
    isMobile,
    lessonId: lessonId ?? undefined,
    transferReason,
    transferStudent,
    reloadBundle,
    setPendingTransfer,
    setTransferReason,
    setTransferredStudentIds,
  });

  // ── Render ─────────────────────────────────────────────────────────
  // Lock attendance editing until 10 minutes before the lesson starts.
  // Strictly time-based: even if some attendance was already saved (or the
  // dialog was opened in edit mode by an upstream caller, e.g. the flexband
  // module-card X icon), we still surface the read-only preview while the
  // lesson is more than 10 minutes away. The diary and the Tagesgast picker
  // remain reachable inside the preview.
  const isFuturePreview = (() => {
    if (!selectedLesson?.start_datetime) return false;
    const start = new Date(selectedLesson.start_datetime);
    const earlyStart = new Date(start.getTime() - 10 * 60 * 1000);
    return earlyStart > new Date();
  })();

  return (
    <DebugOverlay id="LES-010" name="AttendanceDialog-LES-010">
      <Dialog open={!!open && !!lessonId} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-h-[90vh] lg:max-h-[80vh] overflow-y-auto overflow-x-hidden max-w-[calc(100vw-0.5rem)] lg:max-w-2xl lg:mx-auto duration-100"
          data-testid="attendance-dialog-modal"
          data-component="AttendanceDialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Debug info panel */}
          {useDebug().isDebugMode && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 text-xs p-2 mb-2 rounded font-mono">
              <strong>Debug Info:</strong>
              <br />
              lesson_id: <span className="select-all">{lessonId}</span>
              <br />
              course_id: {selectedLesson?.course_id || "null"}
              <br />
              class_id: {selectedLesson?.class_id || "null"}
            </div>
          )}

          <DialogHeader>
            <DialogTitle className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 pr-8">
              <DialogHeaderSection
                selectedLesson={selectedLesson}
                isMobile={isMobile}
                badgeCounts={badgeCounts}
                recordedMeta={recordedMeta}
                attendanceViewMode={attendanceViewMode}
                overviewData={overviewData}
                canEditExisting={canEditExisting}
                onSwitchToEdit={switchToEdit}
                isFuturePreview={isFuturePreview}
              />
            </DialogTitle>
          </DialogHeader>

          {!!selectedLesson && (
            <div>
              {attendanceViewMode === "overview" || isFuturePreview ? (
                <OverviewSection
                  overviewData={overviewData}
                  selectedLesson={selectedLesson}
                  recordedMeta={recordedMeta}
                  prefilledFromPrevious={prefilledFromPrevious}
                  prefilledAny={prefilledAny}
                  diaryMeta={diaryMeta}
                  formatStudentName={formatStudentName}
                  courseOverlaps={courseOverlaps}
                  concurrentCourseAttendance={concurrentCourseAttendance}
                  transferredStudentIds={transferredStudentIds}
                  lessonId={lessonId ?? undefined}
                  isTransferring={isTransferring}
                  pendingTransfer={pendingTransfer}
                  setPendingTransfer={setPendingTransfer}
                  transferReason={transferReason}
                  setTransferReason={setTransferReason}
                  onTransferConfirm={handleTransferConfirm}
                  isFuturePreview={isFuturePreview}
                  protectionByStudent={protectionByStudent}
                  lessonNote={lessonNote}
                  setLessonNote={setLessonNote}
                  canEditDiary={canEditExisting}
                  keptInMainLesson={keptInMainLesson}
                  reloadBundle={reloadBundle}
                />
              ) : (
                <EditSection
                  selectedLesson={selectedLesson}
                  isMobile={isMobile}
                  recordedMeta={recordedMeta}
                  prefilledFromPrevious={prefilledFromPrevious}
                  prefilledAny={prefilledAny}
                  skipPrefillSummary={skipPrefillSummary}
                  diaryMeta={diaryMeta}
                  lessonNote={lessonNote}
                  setLessonNote={setLessonNote}
                  lessonHomework={lessonHomework}
                  setLessonHomework={setLessonHomework}
                  initialLessonHomework={initialLessonHomework}
                  tempAttendance={tempAttendance}
                  setTempAttendance={setTempAttendance}
                  kbStudentIds={kbStudentIds}
                  kbStudentTimes={kbStudentTimes}
                  earlierPresentIds={earlierPresentIds}
                  frozenEditOrderRef={frozenEditOrderRef}
                  frozenEditLessonIdRef={frozenEditLessonIdRef}
                  saveAttendance={saveAttendance}
                  setStudentStatus={setStudentStatus}
                  setAllStudentsStatus={setAllStudentsStatus}
                  formatStudentName={formatStudentName}
                  courseOverlaps={courseOverlaps}
                  concurrentCourseAttendance={concurrentCourseAttendance}
                  transferredStudentIds={transferredStudentIds}
                  lessonId={lessonId ?? undefined}
                  isTransferring={isTransferring}
                  pendingTransfer={pendingTransfer}
                  setPendingTransfer={setPendingTransfer}
                  transferReason={transferReason}
                  setTransferReason={setTransferReason}
                  onTransferConfirm={handleTransferConfirm}
                  nextLessonCourses={nextLessonCourses}
                  nextLessonIsCancelled={nextLessonIsCancelled}
                  nextLessonSubject={nextLessonSubject}
                  keptInMainLesson={keptInMainLesson}
                  reloadBundle={reloadBundle}
                  onReturnToCourseConfirm={handleReturnToCourseConfirm}
                  handleStayedWithClassTransfer={handleStayedWithClassTransfer}
                  protectionByStudent={protectionByStudent}
                  userIdOverride={userIdOverride}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DebugOverlay>
  );
}
